import React, { useState } from 'react';
import { arrayOf, shape, string, object, func, bool } from 'prop-types';
import { propTypes } from '../../../../util/types';
import { FormattedMessage } from '../../../../util/reactIntl';
import { IconComments, NamedLink } from '../../../../components';
import { CommunityCommentForm } from '../../../../forms';
import { convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import classNames from 'classnames';

import Comment from './Comment/Comment';
import css from './Comments.module.css';

/**
 * Renders the no comments message when there are no comments
 */
const NoCommentsSection = () => (
  <div className={css.noComments}>
    <IconComments className={css.commentIcon} />
    <h3 className={css.noCommentsTitle}>
      <FormattedMessage id="PostPage.noCommentsTitle" />
    </h3>
    <p className={css.noCommentsText}>
      <FormattedMessage id="PostPage.noCommentsText" />
    </p>
  </div>
);

/**
 * Comments component displays a list of comments and a form to add new comments.
 * Handles nested comments by only rendering top-level comments directly and
 * letting the Comment component handle the recursive rendering of replies.
 */
const Comments = props => {
  const {
    // Styling props
    rootClassName,
    className,

    // Context props
    intl,
    state,
    location,
    currentUser,
    commentDisplayName,

    // Data props
    comments,

    // UI state props
    showPostInProgress,
    addCommentError,
    deleteCommentInProgress,
    deleteCommentError,
    deletedCommentIds,
    currentDeleteCommentId,
    editCommentInProgress,
    editCommentError,

    // Action handlers
    onAddComment,
    onDeleteComment,
    onEditComment,
    onVoteComment,
    postId,
    onOpenConfirmationModalWithHandlers,
    onUpdateConfirmationModalProps,
    onCloseConfirmationModal,
  } = props;

  // Local state for tracking comment submission
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  // Filter to get only top-level comments (those with no parentId)
  const topLevelComments = comments.filter(comment => !comment.parentId);
  const hasComments = comments.length > 0;
  const classes = classNames(rootClassName || css.root, className);
  const shouldShowLoginPrompt = !currentUser?.id && !showPostInProgress;

  /**
   * Handle submitting a new top-level comment
   */
  const handleSubmitComment = async values => {
    let html;
    
    if (typeof values.content === 'string') {
      // New Lexical editor outputs HTML directly
      html = values.content;
    } else if (values.content.getCurrentContent) {
      // Legacy Draft.js format
      html = draftToHtml(convertToRaw(values.content.getCurrentContent()));
    } else {
      html = '';
    }
    
    try {
      setIsSubmittingComment(true);

      return await onAddComment({
        content: html,
        parentId: null,
      });
    } finally {
      setIsSubmittingComment(false);
    }
  };

  return (
    <div className={classes} id="comments">
      {/* Comment submission form */}
      <div className={css.form}>
        {currentUser?.id ? (
          <CommunityCommentForm
            onSubmit={handleSubmitComment}
            addCommentError={addCommentError}
            addCommentInProgress={isSubmittingComment}
            commentDisplayName={commentDisplayName}
            commentPlaceholderMessage={intl.formatMessage({
              id: 'CommunityCommentForm.commentPlaceholder',
            })}
          />
        ) : (
          shouldShowLoginPrompt && (
            <p className={css.loginToComment}>
              <FormattedMessage
                id="PostPage.loginToComment"
                values={{
                  loginLink: (
                    <NamedLink name="LoginPage" to={{ state: { from: location?.pathname } }}>
                      <FormattedMessage id="PostPage.loginLink" />
                    </NamedLink>
                  ),
                }}
              />
            </p>
          )
        )}
      </div>

      {/* Comments section */}
      {!showPostInProgress && (
        <div className={css.comments}>
          {hasComments ? (
            // Only render top-level comments here, child comments are rendered recursively
            topLevelComments.map(comment => (
              <Comment
                key={comment.id}
                currentUser={currentUser}
                comment={comment}
                allComments={comments} // Pass all comments to find replies
                state={state}
                intl={intl}
                onAddComment={onAddComment}
                onDeleteComment={onDeleteComment}
                onEditComment={onEditComment}
                onVoteComment={onVoteComment}
                postId={postId}
                addCommentError={addCommentError}
                deleteCommentInProgress={deleteCommentInProgress}
                deleteCommentError={deleteCommentError}
                deletedCommentIds={deletedCommentIds}
                currentDeleteCommentId={currentDeleteCommentId}
                editCommentInProgress={editCommentInProgress}
                editCommentError={editCommentError}
                onOpenConfirmationModalWithHandlers={onOpenConfirmationModalWithHandlers}
                onUpdateConfirmationModalProps={onUpdateConfirmationModalProps}
                onCloseConfirmationModal={onCloseConfirmationModal}
              />
            ))
          ) : (
            <NoCommentsSection />
          )}
        </div>
      )}
    </div>
  );
};

Comments.defaultProps = {
  rootClassName: null,
  className: null,
  comments: [],
  commentDisplayName: null,
  location: null,
  currentUser: null,
  addCommentError: null,
  deleteCommentError: null,
  currentDeleteCommentId: null,
  deletedCommentIds: [],
  showPostInProgress: false,
  deleteCommentInProgress: false,
  editCommentInProgress: false,
  editCommentError: null,
  onOpenConfirmationModalWithHandlers: null,
  onUpdateConfirmationModalProps: null,
  onCloseConfirmationModal: null,
};

Comments.propTypes = {
  // Styling props
  rootClassName: string,
  className: string,

  // Context props
  state: object.isRequired,
  location: shape({
    pathname: string,
  }),
  currentUser: propTypes.currentUser,
  commentDisplayName: object,

  // Data props
  comments: arrayOf(
    shape({
      id: string.isRequired,
      userId: string.isRequired,
      content: string, // Can be null for soft-deleted comments
      parentId: string, // For reply structure
      postedAt: string.isRequired,
      isDeleted: bool, // Indicates a soft-deleted comment
      deletedAt: string, // Timestamp for when comment was deleted
    })
  ),

  // UI state props
  showPostInProgress: bool,
  addCommentError: propTypes.error,
  deleteCommentInProgress: bool,
  deleteCommentError: propTypes.error,
  deletedCommentIds: arrayOf(string),
  currentDeleteCommentId: string,
  editCommentInProgress: bool,
  editCommentError: propTypes.error,

  // Action handlers
  onAddComment: func.isRequired,
  onDeleteComment: func.isRequired,
  onEditComment: func.isRequired,
  onVoteComment: func.isRequired,
  postId: string.isRequired,
  onOpenConfirmationModalWithHandlers: func,
  onUpdateConfirmationModalProps: func,
  onCloseConfirmationModal: func,
};

export default Comments;
